// Code to convert state to Plotly layout for a timeline chart

import Plotly, { PlotData, Shape } from "plotly.js";

import { ChartTimeline, TimelineChartState } from "./reducer";

export function stateToPlotlyLayout({
  start,
  end,
  verticalLines = [],
  large,
  yAxes,
  charts,
  width,
  height,
  isCumulative,
  showlegend,
  legend,
  hoverdistance,
  datarevision,
}: TimelineChartState) {
  const layout: Partial<Plotly.Layout> = {
    width,
    height,
    yaxis: getYAxis(yAxes[0], charts, "left"),
    showlegend,
    legend,
    font: {
      size: 10,
      color: "#676767",
    },
    margin: large
      ? {
          t: 10,
          l: 50,
          r: 50,
          b: 30,
        }
      : {
          t: 0,
          r: 0,
          b: 25,
          l: 35,
        },
    xaxis: {
      visible: true,
      type: "date" as const,
      showgrid: true,
      range: [start, end],
      tickfont: { size: width <= 314 ? 7 : 10 },
    },
    bargap: 0,
    dragmode: "zoom",
    hoverdistance: hoverdistance ?? 10, // Default hover distance for tooltips
    datarevision,
  };

  const yaxis2 = getYAxis(yAxes[1], charts, "right");
  if (yaxis2) {
    layout.yaxis2 = yaxis2;
  }

  const data = charts.map((chart) => {
    const isFloat = chart.data.some((event) => !Number.isInteger(event.value));
    const plotlyEvents: Partial<PlotData> = {
      x: chart.data.map((event) => event.time),
      y: chart.data.map((event) => event.value),
      name: chart.title,
      showlegend: true,
      hoverinfo: large ? "x+y+name" : "none",
      hoverlabel: {
        namelength: -1,
      },
      hovertemplate: large && isFloat ? "%{x}, %{y:.2f}" : undefined,
    };

    if (yAxes.length > 1 && yAxes[1] === chart.unit) {
      plotlyEvents.yaxis = "y2";
    } else {
      plotlyEvents.yaxis = "y";
    }

    if (chart.scale === "ratio") {
      // Bar plot
      plotlyEvents.type = "bar";
      plotlyEvents.marker = {
        color: chart.color,
      };
      // Set offset to 0 to align the bars with the start of the x-axis ticks
      plotlyEvents.offset = 0;
      // Set width of bars to time between two data points, assume equidistant.
      if ("x" in plotlyEvents && plotlyEvents.x && plotlyEvents.x.length >= 2) {
        const x = plotlyEvents.x as Date[];
        const barWidthMs = x[1].getTime() - x[0].getTime();
        plotlyEvents.width = barWidthMs;
      }
    } else {
      // Line plot
      plotlyEvents.type = "scatter";
      plotlyEvents.mode = "lines+markers";
      plotlyEvents.marker = {
        size: 3,
      };
      plotlyEvents.line = {
        color: chart.color,
      };
    }

    return plotlyEvents;
  });

  // Add cumulative graphs for ratio data
  if (isCumulative) {
    data.push(
      ...charts
        .filter((chart) => chart.scale === "ratio")
        .map((chart) => {
          let cumulativeValue = 0;
          const cumulativeValues = chart.data.map((event) => {
            if (event.value !== null) cumulativeValue += event.value;
            return cumulativeValue;
          });
          return {
            x: chart.data.map((event) => event.time),
            y: cumulativeValues,
            name: chart.unit,
            showlegend: false,
            hoverinfo: "x+y+name",
            hoverlabel: {
              namelength: -1,
            },
            hovertemplate: "%{x}, %{y:.2f}",
            mode: "lines",
            type: "scatter",
            line: {
              color: chart.color,
            },
          } as Partial<PlotData>;
        })
    );
  }

  const yValues = data
    .map((d) => d.y)
    .filter((y) => y !== null)
    .flat() as number[];
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  // Horizontal threshold lines from timeseries assets
  const thresholdLines = charts
    .map((chart) => chart.thresholds)
    .flat()
    .filter((threshold, i, self) => i === self.findIndex((t) => t.value === threshold.value))
    .filter(({ value }) => value >= minY && value <= maxY)
    .map(({ name, value, unit }) => ({
      type: "line",
      x0: 0,
      x1: 1,
      xref: "paper",
      y0: value,
      y1: value,
      yref: yAxes.length > 1 && yAxes[1] === unit ? "y2" : "y",
      line: {
        color: "grey",
        width: 2,
        dash: "dash",
      },
      label: {
        text: `${name}: ${value} ${unit}`,
        textposition: "end",
      },
    })) as Shape[];

  // Vertical lines, e.g. to indicate the current time
  verticalLines.forEach((line) => {
    thresholdLines.push({
      type: "line",
      x0: line.time,
      x1: line.time,
      y0: 0,
      y1: 1,
      yref: "paper",
      line: {
        color: line.color,
        width: line.width,
        dash: line.dash,
      },
    } as Shape);
  });

  layout.shapes = thresholdLines;

  const chartIsLoading = charts.find((chart) => chart.isLoading);
  const chartIsEmpty = !chartIsLoading && charts.every((chart) => chart.data.length === 0);

  return {
    layout: layout as Plotly.Layout,
    chartIsLoading,
    chartIsEmpty,
    data,
  };
}

function getYAxis(yAxis: string | undefined, charts: ChartTimeline[], side: "left" | "right") {
  // Find a chart on this axis
  if (yAxis === undefined) return undefined;
  const chart = charts.find((chart) => chart.unit === yAxis);
  if (chart === undefined) return undefined;

  const isRatio = chart.scale === "ratio";

  return {
    title: { text: yAxis, font: { size: 10 } },
    tickfont: { size: 10 },
    type: "linear" as const,
    rangemode: isRatio ? ("tozero" as const) : ("normal" as const),
    side,
    overlaying: side === "right" ? ("y" as const) : undefined,
    ticks: "outside" as const,
    tick0: isRatio ? 0 : undefined,
    showgrid: side === "left",
    zeroline: false,
    fixedrange: true,
    visible: true,
  };
}
